import { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Shell";
import { useFetch, Field } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Save, Check, X, Unlock, ClipboardCheck } from "lucide-react";

const STATUS = {
  pending: { cls: "bg-amber-100 text-amber-800 border-amber-300", en: "Pending", fr: "En attente" },
  approved: { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", en: "Approved · Locked", fr: "Approuvé · Verrouillé" },
  rejected: { cls: "bg-rose-100 text-rose-800 border-rose-300", en: "Returned", fr: "Renvoyé" },
  unlocked: { cls: "bg-blue-100 text-blue-800 border-blue-300", en: "Unlocked", fr: "Déverrouillé" },
};

export function GradeSubmissionBadge({ status, L }) {
  const s = STATUS[status];
  if (!s) return null;
  return <Badge className={`border ${s.cls} hover:${s.cls}`} data-testid={`gsub-badge-${status}`}>{L(s.en, s.fr)}</Badge>;
}

export default function GradeApproval() {
  const { L } = useLang();
  const { user } = useAuth();
  const isMaster = user?.role === "admin";
  const { data: subs, loading, reload } = useFetch(() => api.get("/grade-submissions"));
  const [w, setW] = useState(null);

  useEffect(() => { api.get("/grading/weights").then(setW).catch(() => {}); }, []);

  const saveWeights = async () => {
    try {
      const r = await api.put("/grading/weights", {
        exam: parseFloat(w.exam), quiz: parseFloat(w.quiz), project: parseFloat(w.project),
        participation: parseFloat(w.participation), quiz_cap: parseInt(w.quiz_cap), project_cap: parseInt(w.project_cap),
      });
      setW(r);
      toast.success(L("Grading weights saved", "Pondération enregistrée"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const act = async (id, kind) => {
    try {
      let note = null;
      if (kind === "reject") { note = window.prompt(L("Reason returned to teacher (optional):", "Motif renvoyé à l'enseignant (facultatif) :")) || null; }
      await api.post(`/grade-submissions/${id}/${kind}`, kind === "unlock" ? {} : { note });
      toast.success(L("Done", "Effectué"));
      reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const total = w ? (parseFloat(w.exam) + parseFloat(w.quiz) + parseFloat(w.project) + parseFloat(w.participation)) : 0;

  return (
    <div className="animate-fade-up space-y-6" data-testid="grade-approval">
      <PageHeader title={L("Grade Approval", "Approbation des notes")}
        description={L("Configure the weighted grade formula and approve teacher grade submissions before they lock.",
          "Configurez la formule pondérée et approuvez les soumissions de notes des enseignants avant verrouillage.")} />

      {w && (
        <div className="bg-card border border-border rounded-lg p-5" data-testid="grading-weights-card">
          <p className="font-heading font-semibold mb-1">{L("Weighted grade formula", "Formule de note pondérée")}</p>
          <p className="text-xs text-muted-foreground mb-4">{L("Applied per subject. Weights renormalise across the categories a teacher actually enters; extra credit is added on top.",
            "Appliquée par matière. Les poids sont renormalisés selon les catégories réellement saisies ; les points bonus s'ajoutent par-dessus.")}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
            <Field label={L("Exam %", "Examen %")}><Input type="number" value={w.exam} onChange={(e) => setW({ ...w, exam: e.target.value })} data-testid="weight-exam" /></Field>
            <Field label={L("Quiz %", "Quiz %")}><Input type="number" value={w.quiz} onChange={(e) => setW({ ...w, quiz: e.target.value })} data-testid="weight-quiz" /></Field>
            <Field label={L("Project %", "Projets %")}><Input type="number" value={w.project} onChange={(e) => setW({ ...w, project: e.target.value })} data-testid="weight-project" /></Field>
            <Field label={L("Participation %", "Participation %")}><Input type="number" value={w.participation} onChange={(e) => setW({ ...w, participation: e.target.value })} data-testid="weight-participation" /></Field>
            <Field label={L("Max quizzes counted", "Quiz max comptés")}><Input type="number" value={w.quiz_cap} onChange={(e) => setW({ ...w, quiz_cap: e.target.value })} data-testid="weight-quiz-cap" /></Field>
            <Field label={L("Max projects counted", "Projets max comptés")}><Input type="number" value={w.project_cap} onChange={(e) => setW({ ...w, project_cap: e.target.value })} data-testid="weight-project-cap" /></Field>
            <div className="flex flex-col">
              <span className={`text-xs mb-1 ${total === 100 ? "text-emerald-700" : "text-amber-700"}`} data-testid="weight-total">{L("Total", "Total")}: {total}%</span>
              <Button onClick={saveWeights} data-testid="save-weights-btn"><Save className="h-4 w-4 mr-1" />{L("Save", "Enregistrer")}</Button>
            </div>
          </div>
          {total !== 100 && <p className="text-xs text-amber-700 mt-2">{L("Tip: the four weights usually total 100%.", "Astuce : les quatre poids totalisent normalement 100 %.")}</p>}
        </div>
      )}

      <div className="bg-card border border-border rounded-lg p-5">
        <p className="font-heading font-semibold mb-3 flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-primary" />{L("Submission queue", "File de soumissions")}</p>
        {loading ? <Empty title={L("Loading…", "Chargement…")} /> : !(subs || []).length ? (
          <Empty title={L("No submissions yet", "Aucune soumission pour l'instant")} description={L("Teacher grade submissions will appear here for review.", "Les soumissions de notes des enseignants apparaîtront ici pour révision.")} />
        ) : (
          <div className="space-y-2">
            {(subs || []).map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-3 border border-border rounded-lg p-3" data-testid={`gsub-row-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{s.subject} · <span className="text-muted-foreground">{s.class_name || s.class_id}</span> · {s.term}</p>
                  <p className="text-xs text-muted-foreground">{L("By", "Par")} {s.teacher_name} · {s.grade_count} {L("grades", "notes")} · {s.student_count} {L("students", "élèves")}{s.note ? ` · ✎ ${s.note}` : ""}</p>
                </div>
                <GradeSubmissionBadge status={s.status} L={L} />
                {s.status === "pending" && (
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => act(s.id, "approve")} data-testid={`gsub-approve-${s.id}`}><Check className="h-4 w-4 mr-1" />{L("Approve", "Approuver")}</Button>
                    <Button size="sm" variant="outline" onClick={() => act(s.id, "reject")} data-testid={`gsub-reject-${s.id}`}><X className="h-4 w-4 mr-1" />{L("Return", "Renvoyer")}</Button>
                  </div>
                )}
                {s.status === "approved" && isMaster && (
                  <Button size="sm" variant="outline" onClick={() => act(s.id, "unlock")} data-testid={`gsub-unlock-${s.id}`}><Unlock className="h-4 w-4 mr-1" />{L("Unlock", "Déverrouiller")}</Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
